import type { ComponentProps } from 'react';
import type React from 'react';

import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconFavorite, iconFavoriteBorder } from '@change-corgi/design-system-legacy/icons';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { buttonResetStyles } from '@change-corgi/design-system-legacy/reset';
import { useIsSunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { PartialComment } from './hooks/useCommentLikes';
import { useCommentLikes } from './hooks/useCommentLikes';

type Props = {
	comment: PartialComment;
	onLikeToggled?: (liked: boolean) => void;
	size?: ComponentProps<typeof Text>['size'];
};

const getIconColor = (liked: boolean, isSunriseThemeEnabled: boolean) => {
	if (isSunriseThemeEnabled) {
		return liked ? 'sunrise.semantic.fill.brand.strong' : 'sunrise.semantic.icon.weak';
	}

	return liked ? 'typography-highlight' : 'typography-secondary';
};

export const CommentLikes: React.FC<Props & ComponentProps<typeof Box>> = ({
	comment,
	onLikeToggled,
	size,
	...rest
}) => {
	const {
		data: { liked, a11yLabel, likesText },
		actions: { toggleLiked },
	} = useCommentLikes({ comment, onLikeToggled });
	const isSunriseThemeEnabled = useIsSunriseTheme();

	return (
		<Box sx={{ display: 'flex', alignItems: 'center' }} {...rest}>
			<button
				type="button"
				sx={buttonResetStyles}
				onClick={toggleLiked}
				data-testid={`toggle-like-button-comment-${comment.id}`}
				data-qa="comment-toggle-like"
				aria-label={a11yLabel}
			>
				<Text sx={{ display: 'flex', alignItems: 'center', gap: 8 }} size="small">
					<Icon
						icon={liked ? iconFavorite : iconFavoriteBorder}
						sx={{ fontSize: '18px', color: getIconColor(liked, isSunriseThemeEnabled) }}
					/>
					<Text color={isSunriseThemeEnabled ? 'sunrise.semantic.typography.strong' : 'typography-secondary'}>
						{likesText}
					</Text>
				</Text>
			</button>
		</Box>
	);
};
